import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const GRADE_ORDER = ['AA', 'AB', 'PB/C', 'PB', 'C'];

const emptyGrades = () => Object.fromEntries(GRADE_ORDER.map(g => [g, ''])) as Record<string, string>;

const emptyForm = {
  title: '', slug: '', processType: '', flavorNotes: '', tastingNotes: '',
  visualDescription: '', imageUrl: '', fermentationMethod: '', fermentationDuration: '',
  fermentationDetails: '', dryingMethod: '', dryingDetails: '', screenSize: '',
  pricePerKg: '0', packagingInfo: '', quantityInStock: '0',
  availabilityStatus: 'AVAILABLE', cuppingScore: '0', detailedDescription: '',
  productionInformation: '', cherrySelection: '', region: 'Kenya', location: '',
  varieties: '', harvestPeriod: '', altitude: '', isFeatured: false,
  gradeBreakdown: emptyGrades(), gradePricing: emptyGrades(),
};

// Merges saved grade data (numbers, only the grades that have values) into the
// full fixed-row editor state (strings, all standard grades present)
function toGradeFormState(saved: Record<string, number> | undefined) {
  const base = emptyGrades();
  for (const [grade, value] of Object.entries(saved || {})) {
    base[grade] = String(value);
  }
  return base;
}

export const CoffeeForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEdit) {
      fetch('/api/coffees?includeArchived=true')
        .then(r => r.json())
        .then(data => {
          const coffee = (Array.isArray(data) ? data : []).find((c: any) => c.id === id);
          if (coffee) {
            setForm({
              ...emptyForm,
              ...coffee,
              pricePerKg: coffee.pricePerKg || coffee.price || '0',
              cuppingScore: String(coffee.cuppingScore || 0),
              gradeBreakdown: toGradeFormState(coffee.gradeBreakdown),
              gradePricing: toGradeFormState(coffee.gradePricing),
            });
          }
        });
    }
  }, [id, isEdit]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleGradeChange = (field: 'gradeBreakdown' | 'gradePricing', grade: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: { ...prev[field], [grade]: value } }));
  };

  // Collapses the editor's string-keyed rows back to {grade: number} with blanks dropped
  const toGradeRecord = (grades: Record<string, string>): Record<string, number> => {
    const out: Record<string, number> = {};
    for (const [grade, value] of Object.entries(grades)) {
      if (value.trim() !== '' && !isNaN(parseFloat(value))) out[grade] = parseFloat(value);
    }
    return out;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    const slug = form.slug || form.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    const gradeBreakdown = toGradeRecord(form.gradeBreakdown);
    const gradePricing = toGradeRecord(form.gradePricing);

    const body = isEdit
      ? { id, ...form, slug, gradeBreakdown, gradePricing }
      : { ...form, slug, gradeBreakdown, gradePricing };

    try {
      const res = await fetch('/api/admin/coffees', {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to save');
      }

      navigate('/admin/coffees');
    } catch (err: any) {
      setError(err.message);
      setSaving(false);
    }
  };

  const inputClass = "w-full px-3.5 py-2.5 border border-[#E8E6E2] rounded-xl bg-[#FAFAF8] text-[#1A1A1A] text-sm focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]/10 focus:border-[#1A1A1A]/30 focus:bg-white transition-all placeholder-[#C4C4C4]";
  const labelClass = "block text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold mb-1.5";

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A] tracking-tight">{isEdit ? 'Edit Coffee' : 'Add Coffee'}</h1>
          <p className="text-sm text-[#A3A3A3] mt-1">{isEdit ? 'Update product details' : 'Add a new coffee product'}</p>
        </div>
        <button onClick={() => navigate('/admin/coffees')} className="text-sm text-[#A3A3A3] hover:text-[#1A1A1A] transition-colors font-medium">
          Back to coffees
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm font-medium flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="flex-shrink-0">
            <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M8 5v3.5M8 10.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-6 shadow-sm shadow-black/[0.03]">
          <h2 className="text-[11px] font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-5">Basic Info</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Name</label>
              <input name="title" value={form.title} onChange={handleChange} required className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Slug</label>
              <input name="slug" value={form.slug} onChange={handleChange} placeholder="auto-generated" className={inputClass} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 mt-4">
            <div>
              <label className={labelClass}>Process</label>
              <input name="processType" value={form.processType} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Cupping Score</label>
              <input name="cuppingScore" type="number" step="0.1" value={form.cuppingScore} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Availability</label>
              <select name="availabilityStatus" value={form.availabilityStatus} onChange={handleChange} className={inputClass}>
                <option value="AVAILABLE">Available</option>
                <option value="PREORDER">Preorder</option>
                <option value="SOLD OUT">Sold Out</option>
                <option value="ARCHIVED">Archived</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-6 shadow-sm shadow-black/[0.03]">
          <h2 className="text-[11px] font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-5">Pricing & Stock</h2>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Price/kg (USD)</label>
              <input name="pricePerKg" type="number" step="0.01" value={form.pricePerKg} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Stock (kg)</label>
              <input name="quantityInStock" value={form.quantityInStock} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Packaging</label>
              <input name="packagingInfo" value={form.packagingInfo} onChange={handleChange} className={inputClass} />
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-6 shadow-sm shadow-black/[0.03]">
          <h2 className="text-[11px] font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-1">Grade Breakdown & Pricing</h2>
          <p className="text-xs text-[#A3A3A3] mb-5">Per-grade harvest volumes and pricing for the public offer list. TT is sold locally at auction and isn't tracked here.</p>
          <div className="space-y-2">
            <div className="grid grid-cols-[4rem_1fr_1fr_1fr] gap-3 items-center px-1">
              <span className={labelClass}>Grade</span>
              <span className={labelClass}>Volume (kg)</span>
              <span className={labelClass}>Price ($/kg)</span>
              <span className={labelClass}>Value</span>
            </div>
            {GRADE_ORDER.map(grade => {
              const kg = parseFloat(form.gradeBreakdown[grade]);
              const price = parseFloat(form.gradePricing[grade]);
              const value = !isNaN(kg) && !isNaN(price) ? kg * price : undefined;
              return (
                <div key={grade} className="grid grid-cols-[4rem_1fr_1fr_1fr] gap-3 items-center">
                  <span className="text-sm font-medium text-[#1A1A1A] px-1">{grade}</span>
                  <input
                    type="number" step="1" placeholder="0"
                    value={form.gradeBreakdown[grade]}
                    onChange={e => handleGradeChange('gradeBreakdown', grade, e.target.value)}
                    className={inputClass}
                  />
                  <input
                    type="number" step="0.01" placeholder="0.00"
                    value={form.gradePricing[grade]}
                    onChange={e => handleGradeChange('gradePricing', grade, e.target.value)}
                    className={inputClass}
                  />
                  <span className="text-sm text-[#6B6B6B] tabular-nums px-1">
                    {value !== undefined ? `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '—'}
                  </span>
                </div>
              );
            })}
            {(() => {
              const totalKg = GRADE_ORDER.reduce((sum, g) => sum + (parseFloat(form.gradeBreakdown[g]) || 0), 0);
              const totalValue = GRADE_ORDER.reduce((sum, g) => {
                const kg = parseFloat(form.gradeBreakdown[g]);
                const price = parseFloat(form.gradePricing[g]);
                return sum + (!isNaN(kg) && !isNaN(price) ? kg * price : 0);
              }, 0);
              if (totalKg === 0 && totalValue === 0) return null;
              return (
                <div className="grid grid-cols-[4rem_1fr_1fr_1fr] gap-3 items-center pt-2 mt-1 border-t border-[#E8E6E2]">
                  <span className="text-sm font-semibold text-[#1A1A1A] px-1">Total</span>
                  <span className="text-sm font-semibold text-[#1A1A1A] tabular-nums px-1">{totalKg.toLocaleString()} kg</span>
                  <span />
                  <span className="text-sm font-semibold text-[#1A1A1A] tabular-nums px-1">
                    ${totalValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                </div>
              );
            })()}
          </div>
        </div>

        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-6 shadow-sm shadow-black/[0.03]">
          <h2 className="text-[11px] font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-5">Description</h2>
          <div className="space-y-4">
            <div>
              <label className={labelClass}>One-liner</label>
              <input name="visualDescription" value={form.visualDescription} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Flavor Notes</label>
              <input name="flavorNotes" value={form.flavorNotes} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Tasting Notes</label>
              <input name="tastingNotes" value={form.tastingNotes} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Detailed Description</label>
              <textarea name="detailedDescription" value={form.detailedDescription} onChange={handleChange} rows={3} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Image URL</label>
              <input name="imageUrl" value={form.imageUrl || ''} onChange={handleChange} className={inputClass} />
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-6 shadow-sm shadow-black/[0.03]">
          <h2 className="text-[11px] font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-5">Origin</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Region</label>
              <input name="region" value={form.region} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Location / Producer</label>
              <input name="location" value={form.location} onChange={handleChange} className={inputClass} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 mt-4">
            <div>
              <label className={labelClass}>Varieties</label>
              <input name="varieties" value={form.varieties} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Altitude</label>
              <input name="altitude" value={form.altitude} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Harvest Period</label>
              <input name="harvestPeriod" value={form.harvestPeriod} onChange={handleChange} className={inputClass} />
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-6 shadow-sm shadow-black/[0.03]">
          <h2 className="text-[11px] font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-5">Processing</h2>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Fermentation Method</label>
              <input name="fermentationMethod" value={form.fermentationMethod} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Fermentation Duration</label>
              <input name="fermentationDuration" value={form.fermentationDuration} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Screen Size</label>
              <input name="screenSize" value={form.screenSize} onChange={handleChange} className={inputClass} />
            </div>
          </div>
          <div className="space-y-4 mt-4">
            <div>
              <label className={labelClass}>Fermentation Details</label>
              <textarea name="fermentationDetails" value={form.fermentationDetails} onChange={handleChange} rows={2} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Drying Method</label>
              <input name="dryingMethod" value={form.dryingMethod} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Production Information</label>
              <textarea name="productionInformation" value={form.productionInformation} onChange={handleChange} rows={2} className={inputClass} />
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-6 shadow-sm shadow-black/[0.03]">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" name="isFeatured" checked={!!form.isFeatured} onChange={handleChange} id="featured" className="w-4 h-4 rounded-md accent-[#1A1A1A]" />
            <div>
              <p className="text-sm font-medium text-[#1A1A1A]">Featured coffee</p>
              <p className="text-xs text-[#A3A3A3]">Show this coffee prominently on the homepage</p>
            </div>
          </label>
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={saving}
            className="px-8 py-3 bg-[#1A1A1A] text-white rounded-xl text-sm font-medium hover:bg-[#2a2a2a] active:scale-[0.98] transition-all disabled:opacity-40 shadow-sm shadow-black/10"
          >
            {saving ? 'Saving...' : isEdit ? 'Update Coffee' : 'Create Coffee'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin/coffees')}
            className="px-6 py-3 border border-[#E8E6E2] rounded-xl text-sm text-[#6B6B6B] hover:bg-[#F5F3F0] hover:border-[#E0DDD8] transition-all font-medium"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};
